import React, { useEffect, useState } from 'react';
import Assignment from 'models/Assignment';
import { TextEditor } from '../../TextEditor';
import ActionButton from 'view/components/buttons/ActionButton';
import Book from 'models/Book';
import { useParams } from 'react-router-dom';
import AssignmentSidebar from '../../AssignmentSidebar';
import {
  faArrowLeft,
  faArrowRight,
  faPencilAlt,
  faSave,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import WritingAssignmentIntroForm from './WritingIntroForm';
import AssignmentTypeSelector from '../../AssignmentTypeSelector';
import { MultipartEditor } from '../multipart/Multipart';
import Question from 'models/Question';
import MultipleChoiceOption from 'models/MultipleChoiceOption';
import { QuestionType } from 'utils/types';

interface WritingAssignmentFormProps {
  questions?: Question[];
  assignment: Assignment;
  book: Book;
  setAssignment: React.Dispatch<React.SetStateAction<Assignment | undefined>>;
  updateAssignment: (event: any) => void;
  updateBook: (event: any) => void;
  handleInputFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleBookCoverChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleCreateAssignment: (isFreeform: boolean) => void;
  handleDeleteAssignment: () => void;
  handleUpdateText: (richText: string) => void;
  onCancel: () => void;
  onGenerateText: (text: string) => void;
  onOpenClassroomSelector: () => void;
  onCreateQuestion: (
    text: string,
    choices?: MultipleChoiceOption[],
    index?: number,
    type?: QuestionType,
  ) => Promise<void>;
  onUpdateQuestion: (
    question: Question,
    submit?: boolean,
    image?: File,
  ) => void;
  onDeleteQuestion: (question: Question) => void;
}

const WritingAssignmentForm: React.FC<WritingAssignmentFormProps> = ({
  assignment,
  book,
  questions = [],
  setAssignment,
  updateAssignment,
  updateBook,
  handleInputFileChange,
  handleBookCoverChange,
  handleCreateAssignment,
  handleDeleteAssignment,
  handleUpdateText,
  onCancel,
  onGenerateText,
  onOpenClassroomSelector,
  onCreateQuestion,
  onUpdateQuestion,
  onDeleteQuestion,
}) => {
  const [isFreeform, setIsFreeform] = useState<boolean>(true);
  const [showIntroScreen, setShowIntroScreen] = useState<boolean>(false);
  const [showBookEditor, setShowBookEditor] = useState<boolean>(false);
  const { assignmentId } = useParams();

  useEffect(() => {
    if (assignmentId) {
      setShowIntroScreen(false);
      setIsFreeform(
        !assignment.book || assignment.book === '' || book.html_url === '',
      );
    } else {
      setShowIntroScreen(true);
    }
  }, [assignmentId, assignment.book, book.html_url]);

  const handleSelectWritingType = (isFreeform: boolean) => {
    onCreateQuestion('', undefined, undefined, QuestionType.WRITING);
    setIsFreeform(isFreeform);
    setShowBookEditor(!isFreeform);
    setShowIntroScreen(false);
  };

  return (
    <div className="app-page-content">
      <AssignmentSidebar
        assignment={assignment}
        book={book}
        options={['key-words', 'cover-image']}
        handleBookCoverChange={handleBookCoverChange}
        handleInputFileChange={handleInputFileChange}
        onCancel={onCancel}
        setAssignment={setAssignment}
        updateAssignment={updateAssignment}
        updateBook={updateBook}
        onOpenClassroomSelector={onOpenClassroomSelector}
      />
      <div className="app-main-content">
        <div className="book-details-editor">
          <div className="assignment-editor-title">
            <FontAwesomeIcon
              icon={faPencilAlt}
              size="3x"
              color="var(--dark-green)"
            />
            <input
              className="title"
              type="text"
              placeholder="Enter your assignment title..."
              id="title"
              value={book?.title ?? ''}
              onChange={(event) => {
                updateBook(event);
                updateAssignment(event);
              }}
            />
          </div>
        </div>
        {showIntroScreen ? (
          <WritingAssignmentIntroForm
            onSelectWritingType={handleSelectWritingType}
          />
        ) : showBookEditor ? (
          <>
            <TextEditor
              initialValue={book?.html_content ?? ''}
              onChange={handleUpdateText}
              onGenerateText={onGenerateText}
            />
          </>
        ) : (
          <MultipartEditor
            questions={questions}
            onCreateQuestion={onCreateQuestion}
            onDeleteQuestion={onDeleteQuestion}
            onUpdateQuestion={onUpdateQuestion}
          />
        )}
        <div className="assignment-editor-actions">
          {assignmentId && (
            <ActionButton
              type="delete"
              onClick={handleDeleteAssignment}
              label="Delete"
              icon={faTrash}
              requiresConfirmation={true}
              confirmationMessage="Are you sure you want to delete this assignment? All submissions and grades will be lost."
            />
          )}
          {!showIntroScreen &&
            (showBookEditor ? (
              <ActionButton
                type="go"
                onClick={() => setShowBookEditor(false)}
                label="Next"
                icon={faArrowRight}
              />
            ) : (
              <>
                {!isFreeform && (
                  <ActionButton
                    type="edit"
                    onClick={() => setShowBookEditor(true)}
                    label="Previous"
                    icon={faArrowLeft}
                  />
                )}
                <ActionButton
                  type="go"
                  onClick={() => handleCreateAssignment(isFreeform)}
                  label={assignmentId ? 'Update' : 'Upload'}
                  icon={faSave}
                />
              </>
            ))}
          {!assignmentId && (
            <AssignmentTypeSelector
              assignmentType={assignment.assignment_type}
              onChange={updateAssignment}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default WritingAssignmentForm;
